import {
  DEFAULT_WINDOW_SIZE,
  HEADER_HEIGHT,
  PHONE_BEZEL_WIDTH,
  STALE_CLEANUP_MS,
} from "../constants";
import type { ImpersonationSession, PersistedSession } from "../types";

const STORAGE_KEY = "impersonation-sessions";
const BALLOON_WIDTH = 220;
const BALLOON_HEIGHT = 36;
const BALLOON_BOTTOM_OFFSET = 80;
const BALLOON_SPACING = 50;
const BALLOON_RIGHT_OFFSET = 20;

let sessionCounter = 0;
export let zIndexCounter = 0;

export function nextZIndex(): number {
  return zIndexCounter++;
}

export function generateSessionId(): string {
  sessionCounter += 1;
  return `impersonation-${Date.now()}-${sessionCounter}`;
}

export function getInitialBalloonPosition(index: number): { x: number; y: number } {
  return {
    x: window.innerWidth - BALLOON_WIDTH - BALLOON_RIGHT_OFFSET,
    y: window.innerHeight - BALLOON_BOTTOM_OFFSET - index * BALLOON_SPACING,
  };
}

export function getInitialWindowPosition(sessionCount: number): { x: number; y: number } {
  const offset = sessionCount * 30;
  return {
    x: Math.max(50, window.innerWidth / 2 - DEFAULT_WINDOW_SIZE.width / 2 + offset),
    y: Math.max(50, 100 + offset),
  };
}

export function clampWindowPosition(
  session: ImpersonationSession,
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  const totalWidth = session.size.width + PHONE_BEZEL_WIDTH * 2;
  const totalHeight = session.size.height + PHONE_BEZEL_WIDTH * 2 + HEADER_HEIGHT;
  return {
    x: Math.max(0, Math.min(session.position.x, viewportWidth - totalWidth)),
    y: Math.max(0, Math.min(session.position.y, viewportHeight - totalHeight)),
  };
}

export function clampBalloonPosition(
  balloonPosition: { x: number; y: number },
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(balloonPosition.x, viewportWidth - BALLOON_WIDTH)),
    y: Math.max(0, Math.min(balloonPosition.y, viewportHeight - BALLOON_HEIGHT)),
  };
}

export function persistSessions(sessions: ImpersonationSession[]): void {
  const toPersist: PersistedSession[] = sessions.map((session) => ({
    id: session.id,
    workerId: session.workerId,
    workerName: session.workerName,
    cognitoUsername: session.cognitoUsername,
    url: session.url,
    startedAt: session.startedAt,
    expiresAt: session.expiresAt,
  }));

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // localStorage may be full or disabled — silently ignore
  }
}

export function loadPersistedSessions(): ImpersonationSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as PersistedSession[];
    const now = Date.now();

    return parsed
      .filter((session) => now - session.startedAt < STALE_CLEANUP_MS)
      .map((session, index) => ({
        ...session,
        isMinimized: true,
        isActive: false,
        position: getInitialWindowPosition(index),
        size: { ...DEFAULT_WINDOW_SIZE },
        balloonPosition: getInitialBalloonPosition(index),
        zIndex: nextZIndex(),
        expiresAt: session.expiresAt,
      }));
  } catch {
    return [];
  }
}
